import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import openingAsset from '@/assets/ova-scroll/opening-square.webp.asset.json';
import posterAsset from '@/assets/ova-scroll/poster-square.webp.asset.json';
import storyMp4Asset from '@/assets/ova-scroll/story-square.mp4.asset.json';
import { useOvaScrollVideo } from './useOvaScrollVideo';
import { useGallerySwapMotion } from './useGallerySwapMotion';
import './ova-scroll-gallery.css';

export type OvaGalleryImage = { src: string; alt: string };
export type OvaScrollGalleryProps = {
  images?: OvaGalleryImage[];
  className?: string;
  /** Total animated gallery height in desktop viewport screens; defaults to two. */
  scrollScreens?: number;
  /** Reserve space for an existing fixed footer, if needed. */
  bottomInset?: number;
  onSlideChange?: (index: number) => void;
  /** Pass the existing share/wishlist controls through unchanged. */
  galleryActions?: ReactNode;
};
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function OvaScrollGallery({ images = [], className = '', scrollScreens = 2, bottomInset, onSlideChange, galleryActions }: OvaScrollGalleryProps) {
  const galleryStyle = {'--ova-scroll-screens': Math.max(2, scrollScreens), ...(bottomInset == null ? {} : {'--ova-gallery-bottom-inset': Math.max(0, bottomInset) + 'px'})} as CSSProperties;
  const [selected, setActive] = useState(0);
  const active = Math.min(selected, images.length);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pointerStart = useRef<{x: number; y: number} | null>(null);
  const alignAfterChange = useRef(false);
  const prepareSwap = useGallerySwapMotion(mediaRef, active);
  const { animated, nearby, ready, viewportInsideRef } = useOvaScrollVideo({sectionRef, stageRef, mediaRef, videoRef, enabled: active === 0});
  const wasAnimated = useRef(false);
  const count = images.length + 1;
  const isStory = active === 0 || images.length === 0;
  const selectedImage = images[Math.min(active - 1, images.length - 1)];

  const select = (index: number) => {
    const next = (index + count) % count;
    if (next === active) return;
    prepareSwap(index - active);
    const rect = sectionRef.current?.getBoundingClientRect();
    const inset = stageRef.current ? parseFloat(getComputedStyle(stageRef.current).top) || 80 : 80;
    alignAfterChange.current = !!rect && rect.top <= inset + 1 && rect.bottom > 0;
    setActive(next);
    onSlideChange?.(next);
  };
  useClientLayoutEffect(() => {
    // Collapsing the story runway must not strand the reader farther down the page.
    const collapsedInView = wasAnimated.current && !animated && viewportInsideRef.current;
    if (alignAfterChange.current || collapsedInView) sectionRef.current?.scrollIntoView({behavior: 'instant', block: 'start'});
    alignAfterChange.current = false;
    wasAnimated.current = animated;
  }, [active, animated, viewportInsideRef]);


  return (
    <div ref={sectionRef} className={["ova-scroll-gallery", className].filter(Boolean).join(" ")} style={galleryStyle} data-motion={animated} data-ready={ready && animated}
      data-story={isStory} data-engagement-section="ova-story">
      <div ref={stageRef} className="ova-gallery-stage">
        <div ref={mediaRef} className="ova-gallery-visual" role="region" aria-roledescription="carousel"
          aria-label="Ova Balance product gallery" tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault(); select(active + (event.key === 'ArrowRight' ? 1 : -1));
            }
          }}
          onPointerDown={event => { pointerStart.current = {x:event.clientX,y:event.clientY}; }}
          onPointerCancel={() => { pointerStart.current = null; }}
          onPointerUp={event => {
            const start = pointerStart.current; pointerStart.current = null;
            if (!start) return;
            const dx = event.clientX - start.x, dy = event.clientY - start.y;
            if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) select(active + (dx < 0 ? 1 : -1));
          }}>
          <img className="ova-gallery-poster" src={isStory ? (animated ? openingAsset.url : posterAsset.url) : selectedImage.src}
            width={1080} height={1080} alt={isStory ? (animated ? 'Your cycle. Your metabolism, too. Introducing Ova Balance.' : 'Ova Balance pack and sachet.') : selectedImage.alt}
            loading={isStory ? 'eager' : 'lazy'} decoding="async" draggable={false} />
          {animated && nearby && <video ref={videoRef} className="ova-gallery-video" width={1080} height={1080}
            muted playsInline preload="auto" poster={openingAsset.url} disablePictureInPicture tabIndex={-1} aria-hidden="true">
            <source src={storyMp4Asset.url} type="video/mp4" />
          </video>}
          {galleryActions && <div className="ova-gallery-actions">{galleryActions}</div>}
          {animated && <span className="ova-gallery-progress" aria-hidden="true"><span /></span>}
        </div>
        <div className="ova-gallery-thumbnails" aria-label="Product images">
          <button type="button" aria-label="Show Ova Balance story" aria-pressed={active === 0} onClick={() => select(0)}>
            <img src={posterAsset.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
          </button>
          {images.map((image, index) => (
            <button key={`${image.src}-${index}`} type="button" aria-label={`Show ${image.alt}`} aria-pressed={active === index + 1} onClick={() => select(index + 1)}>
              <img src={image.src} alt="" width={56} height={56} loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
        <p className="ova-gallery-transcript">Your cycle. Your metabolism, too. Meet Ova Balance. Daily support for both. Myo-inositol, D-chiro inositol and berberine. Active folate supports normal cell division. Magnesium supports normal muscle and nerve function. Zinc supports normal immune function. Metabolic health and cycle support, alongside your healthy routine. One simple step to add. Make room for your wellbeing. Start your daily balance ritual. With Ova Balance. Food supplement. Not a medicine.</p>
      </div>
    </div>
  );
}
